import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/data";

const LINKS = [
  { label: "Servizio", id: "solution" },
  { label: "Vantaggi", id: "benefits" },
  { label: "Portfolio", id: "gallery" },
  { label: "FAQ", id: "faq" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0A0E14]/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-3"
        >
          <img src={LOGO_URL} alt="copypro3d.it" className="h-11 w-11 rounded-full object-cover" />
          <span className="font-display font-bold text-lg tracking-tight hidden sm:block">
            copypro<span className="text-[#00C8D7]">3d</span>.it
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="font-mono-tech text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-quote"
            onClick={() => scrollTo("quote")}
            className="hidden sm:inline-flex items-center bg-[#00C8D7] text-[#0A0E14] font-bold text-sm px-5 py-2.5 hover:bg-[#00C8D7]/85 transition-colors"
          >
            Richiedi preventivo
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#0A0E14]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="font-mono-tech text-sm uppercase tracking-[0.15em] text-slate-300 text-left"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-cta-quote"
                onClick={() => { scrollTo("quote"); setOpen(false); }}
                className="bg-[#00C8D7] text-[#0A0E14] font-bold text-sm px-5 py-3 mt-2"
              >
                Richiedi preventivo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
